import { type FC, useState, useEffect } from 'react';
import { getExtensionFromMimeType } from '../../../modules/widgets/Uploader/core/UploaderUtils';
import { setupLogo } from '../core/_requests';
import DragAndDropLogoUploader from './DragAndDropLogoUploader';
import { type FormikProps } from 'formik';
import { type SetupRequestDto } from '../../../models/SetupRequestDto';
import type { AxiosProgressEvent } from 'axios';

type FileWithStatus = {
  name: string;
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  size: number;
};

type Props = {
  formik: FormikProps<SetupRequestDto>;
};

const Step3: FC<Props> = ({ formik }) => {
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const allowedExtensions = ['jpg', 'jpeg', 'png', 'svg', 'webp'];

  const isValidFileExtension = (blob: Blob): boolean => {
    if (!allowedExtensions) return true;
    return allowedExtensions.includes(getExtensionFromMimeType(blob.type));
  };

  const uploadFiles = async (filesToUpload: FileWithStatus[]) => {
    // setIsUploading(true);
    const uploadPromises = filesToUpload.map(async (fileWithStatus, index) => {
      if (fileWithStatus.status !== 'pending') return;

      try {
        const { data } = await setupLogo(fileWithStatus.file, (progressEvent: AxiosProgressEvent) => {
          const percentCompleted = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setFiles((prevFiles) => {
            const newFiles = [...prevFiles];
            newFiles[index] = { ...newFiles[index], status: 'uploading', progress: percentCompleted };
            return newFiles;
          });
        });

        formik.setFieldValue('logo', { id: data.id });

        setFiles((prevFiles) => {
          const newFiles = [...prevFiles];
          newFiles[index] = { ...newFiles[index], status: 'success', progress: 100 };
          return newFiles;
        });
      } catch (error) {
        console.error('Upload failed:', error);
        setFiles((prevFiles) => {
          const newFiles = [...prevFiles];
          newFiles[index] = { ...newFiles[index], status: 'error', progress: 0 };
          return newFiles;
        });
      }
    });

    await Promise.all(uploadPromises);
    // setIsUploading(false);
  };

  const handleFileUpload = (newFiles: File[]) => {
    if (newFiles.length > 1) {
      setErrorMessage('Apenas um arquivo por vez é permitido.');
      return;
    }

    const validFiles = newFiles.filter(isValidFileExtension);
    if (validFiles.length !== newFiles.length) {
      setErrorMessage(
        allowedExtensions
          ? `Apenas arquivos ${allowedExtensions.join(', ')} são permitidos.`
          : 'Todos os formatos de arquivos são permitidos.',
      );
      return;
    }

    const newFilesWithStatus: FileWithStatus[] = validFiles.map((file) => ({
      name: file.name,
      file,
      status: 'pending',
      progress: 0,
      size: file.size,
    }));

    setFiles(newFilesWithStatus);
    uploadFiles(newFilesWithStatus);
  };

  const removeLogo = () => {
    setFiles([]);
    setPreviewUrl(null);
  };

  // Gera a URL de preview e limpa quando não for mais necessária
  useEffect(() => {
    if (files.length > 0) {
      const url = URL.createObjectURL(files[0].file);
      setPreviewUrl(url);

      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [files]);

  return (
    <div className="d-flex flex-column w-100">
      <div className="d-flex align-items-center mb-6">
        <h3 className="stepper-title me-7">Logo e estilo</h3>
        <div className="stepper-desc opacity-50">Deixe o sistema com a cara da sua empresa</div>
      </div>
      <div className="card h-auto d-flex align-items-center justify-content-center">
        <div className="card-body p-4 p-lg-10 w-100">
          <div className="d-flex flex-column align-items-center justify-content-center">
            <div>
              {previewUrl ? (
                <img
                  alt="Logo Preview"
                  className="mb-4"
                  src={previewUrl}
                  style={{ maxWidth: '200px', maxHeight: '200px' }}
                />
              ) : null}
              <DragAndDropLogoUploader
                accept={allowedExtensions}
                hasLogo={!!previewUrl}
                onFileUpload={handleFileUpload}
                onRemoveLogo={removeLogo}
              />
            </div>
            <h3 className="stepper-title fs-4 text-center">Personalize a experiência da sua empresa!</h3>
            <div className="d-flex flex-column gap-2">
              <div className="text-gray-700 text-center">
                Adicione a logo da sua empresa para que o sistema fique com a identidade visual do seu negócio, tornando
                o uso mais acolhedor para sua equipe.
              </div>
              <div className="text-gray-700 text-center">
                Clique em “Adicionar logo” para fazer o upload da sua marca e deixe tudo com o jeito da sua empresa!
              </div>
            </div>
          </div>
        </div>
      </div>
      {errorMessage ? <div className="alert alert-danger mt-3">{errorMessage}</div> : null}
    </div>
  );
};

export default Step3;
